<script setup lang="ts">
import SVGRight from '@mop/shared/images/arrow/right.svg?component';
import SVGLeft from '@mop/shared/images/arrow/left.svg?component';
import SVGMOPLogo from '@mop/shared/images/logo/logo_mop_signet.svg?component';

defineOptions({
  name: 'MopHeaderMobileMenuOverlay',
});

const currentLevelRef = ref(0);
const currentRootRef = ref();
const { activePathRef } = useMopNavigation();
const router = useRouter();

watch(
  () => activePathRef.value.length,
  () => {
    if (!activePathRef.value.length) {
      return;
    }
    currentRootRef.value = activePathRef.value[0];
    currentLevelRef.value = activePathRef.value.length > 1 ? 1 : 0;
  },
  { immediate: true },
);

function handleNavigation(item: any) {
  if (currentLevelRef.value === 0 && item.hasChildren()) {
    currentLevelRef.value = 1;
    currentRootRef.value = item;
  } else {
    router.push(item.getLink().to);
  }
}

function handleBack() {
  currentLevelRef.value = 0;
  currentRootRef.value = undefined;
}
</script>
<template>
  <div class="mobile-menu">
    <MopHeaderMobileMenuSlider :active-level="currentLevelRef">
      <nav class="mobile-menu__nav">
        <div v-for="item in $navigationList" :key="item.getUid()">
          <template v-if="item.getLink().isLink">
            <UiNuxtLink v-bind="item.getLink()" class="mobile-menu__nav-item" @click="() => handleNavigation(item)">
              {{ item.getName() }}
              <SVGRight v-if="item.hasChildren()" width="26" height="26" />
            </UiNuxtLink>
          </template>
          <template v-else>
            <UiNuxtLink v-bind="item.getLink()" class="mobile-menu__nav-item">{{ item.getName() }}</UiNuxtLink>
          </template>
        </div>
      </nav>
      <nav v-if="currentLevelRef === 1 && currentRootRef" class="mobile-menu__nav">
        <UiNuxtLink
          v-bind="currentRootRef.getLink()"
          class="mobile-menu__nav-item mobile-menu__nav-item--back"
          @click.prevent="handleBack"
        >
          <SVGLeft width="26" height="26" />
          {{ currentRootRef.getName() }}</UiNuxtLink
        >
        <UiNuxtLink
          v-if="currentRootRef.getChildren()?.[0]?.getLink()?.to !== currentRootRef.getLink()?.to"
          v-bind="currentRootRef.getLink()"
          class="navigation-tree__item"
          @click="() => handleNavigation(currentRootRef)"
        >
          {{ $mopI18n.t('locale.menu.overview', [currentRootRef.getName()]) }}</UiNuxtLink
        >

        <MopNavigationTree :nav-item="currentRootRef" />
      </nav>
    </MopHeaderMobileMenuSlider>
    <div class="mobile-menu__contact">
      <SVGMOPLogo width="70" height="70" />
    </div>
  </div>
</template>
<style lang="scss" scoped>
@mixin getSidePadding {
  padding-left: $global-padding;
  padding-right: $global-padding;
}

.mobile-menu {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: $space40 + $mobile-header-height;
}

.mobile-menu__nav {
  @include getSidePadding();

  display: flex;
  flex-direction: column;
  width: 25%;

  :deep(.navigation-tree--2) {
    padding-left: $space25;
  }

  :deep(.navigation-tree--3) {
    padding-left: $space10;
  }

  :deep(.navigation-tree__item) {
    @include text-style(weighted-bold);
    @include link-not-underlined(0);

    display: block;
    margin-top: $space15;
    text-transform: uppercase;

    // stylelint-disable-next-line
    &::after {
      display: none;
    }
  }

  :deep(.navigation-tree__item--active) {
    @include link-underlined(0);

    // stylelint-disable-next-line
    &::after {
      display: block;
    }
  }
}

.mobile-menu__nav-item {
  @include text-style(weighted-bold);

  text-transform: uppercase;
  font-size: 22px;
  line-height: 32px;
  margin-top: $space10;
  display: flex;
  align-items: center;
}

.mobile-menu__nav-item--back {
  margin-left: -#{$space5};
}

.mobile-menu__contact {
  @include getSidePadding();

  display: flex;
  flex-direction: column;
  margin-top: $space30;
}
</style>
